import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alan, Buton, Kart } from "@koclukpanel/ui";
import {
  ApiHatasi,
  bakimAc,
  bakimDurumu,
  bakimKapat,
  guvenilirCihazlariIptalEt,
  tumOturumlariSonlandir,
} from "./api";
import { useSudoKapisi } from "./SudoKapisi";

/**
 * ACİL DURUM İŞLEMLERİ — F-SEC-06
 *
 * ================================================================
 * İKİ ADIMLI ONAY ARAYÜZDE, KORUMA DEĞİL
 * ================================================================
 * Onay kutusu bir **kaza önleyici**: yanlışlıkla tıklamayı engelliyor.
 * Gerçek koruma sunucuda ve üç katmanlı — izin, sudo (şifre) ve
 * senkron denetim kaydı.
 *
 * Bu ayrım yazılı olmasaydı biri bir gün "sunucu zaten onay istiyor"
 * sanıp koruma katmanını kaldırabilirdi. Sunucu onay İSTEMİYOR;
 * isteseydi de işe yaramazdı, çünkü "onayladım" cevabı istemciden
 * gelirdi.
 *
 * ================================================================
 * NE YAPACAĞI ÖNCEDEN YAZILI
 * ================================================================
 * Her düğmenin altında **ne olacağı** duruyor, "emin misiniz" değil.
 * "Emin misiniz" sorusu hiçbir bilgi vermiyor; bu ekranda kullanıcı
 * kaç kişinin etkileneceğini ve kendisinin de düşüp düşmeyeceğini
 * bilerek basmalı.
 */

interface AcilIslem {
  anahtar: string;
  baslik: string;
  /** NE OLACAĞI — "emin misiniz" değil. */
  sonuc: string;
  dugme: string;
  calistir: (gerekce: string) => Promise<string>;
}

const ISLEMLER: readonly AcilIslem[] = [
  {
    anahtar: "oturumlar",
    baslik: "Tüm oturumları sonlandır",
    sonuc:
      "Sistemdeki herkes anında çıkışa düşer ve yeniden giriş yapar — SİZ DE DAHİL. Bu ekrandan atılırsınız. Şifreler değişmez, iki adımlı doğrulama etkilenmez.",
    dugme: "Herkesi çıkışa düşür",
    calistir: async (gerekce) => {
      const s = await tumOturumlariSonlandir(gerekce);
      return `${s.kapatilan} oturum kapatıldı. Sizin oturumunuz da kapandı; bir sonraki isteğinizde giriş ekranına döneceksiniz.`;
    },
  },
  {
    anahtar: "cihazlar",
    baslik: "Tüm güvenilir cihazları iptal et",
    sonuc:
      "Herkes bir sonraki girişinde iki adımlı doğrulama kodu girer. Açık oturumlar KAPANMAZ; bu işlem yalnızca 'bu cihazı hatırla' kayıtlarını siler.",
    dugme: "Cihaz kayıtlarını sil",
    calistir: async (gerekce) => {
      const s = await guvenilirCihazlariIptalEt(gerekce);
      return `${s.iptalEdilen} güvenilir cihaz kaydı iptal edildi.`;
    },
  },
];

function AcilKart({ islem }: { islem: AcilIslem }) {
  const qc = useQueryClient();
  /*
    ÜÇ HÂL, İKİ DEĞİL: kapalı · onay bekliyor · gerekçe yazılıyor.
    Tek `acik` bayrağı kullansaydım, "düğmeye bastım ve iş oldu" ile
    "düğmeye bastım, şimdi gerekçe yazıyorum" aynı hâl olurdu.
  */
  const [asama, setAsama] = useState<"kapali" | "onay">("kapali");
  const [gerekce, setGerekce] = useState("");
  const [sonuc, setSonuc] = useState<string | null>(null);
  const { hataYakala, kapi } = useSudoKapisi("Acil durum işlemi için şifrenizi doğrulayın.");

  const calistir = useMutation({
    mutationFn: () => islem.calistir(gerekce),
    onError: hataYakala(() => calistir.mutate()),
    onSuccess: async (mesaj) => {
      setSonuc(mesaj);
      setAsama("kapali");
      setGerekce("");
      await qc.invalidateQueries({ queryKey: ["guvenlik-pano"] });
      await qc.invalidateQueries({ queryKey: ["guvenlik-oturumlar"] });
    },
  });

  return (
    <div className="border-notr-200 flex flex-col gap-2 border-b py-3 last:border-b-0">
      {kapi}
      <div>
        <p className="text-notr-900 text-sm font-medium">{islem.baslik}</p>
        <p className="text-notr-600 mt-0.5 text-xs">{islem.sonuc}</p>
      </div>

      {asama === "kapali" ? (
        <div>
          <Buton gorunum="ikincil" onClick={() => setAsama("onay")}>
            {islem.dugme}
          </Buton>
        </div>
      ) : (
        <div className="bg-uyari-50 rounded-alan flex flex-col gap-2 p-3">
          <p className="text-uyari-800 text-sm font-medium">
            Bu işlem geri alınamaz. Devam etmek için gerekçe yazın.
          </p>
          <Alan
            etiket="Gerekçe"
            yardim="Denetim kaydına yazılır ve silinemez. Bu ölçekte bir müdahalenin sebebi sonradan mutlaka sorulur."
            value={gerekce}
            onChange={(e) => setGerekce(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Buton
              onClick={() => calistir.mutate()}
              yukleniyor={calistir.isPending}
              yukleniyorMetni="Uygulanıyor"
              disabled={gerekce.trim().length === 0}
            >
              Evet, {islem.dugme.toLocaleLowerCase("tr")}
            </Buton>
            <Buton
              gorunum="ikincil"
              onClick={() => {
                setAsama("kapali");
                setGerekce("");
              }}
            >
              Vazgeç
            </Buton>
          </div>
        </div>
      )}

      {calistir.isError && (
        <p role="alert" className="text-hata-700 text-sm">
          {calistir.error instanceof ApiHatasi ? calistir.error.message : "İşlem yapılamadı."}
        </p>
      )}
      {sonuc && (
        <p role="status" className="text-notr-700 text-sm">
          {sonuc}
        </p>
      )}
    </div>
  );
}

/**
 * BAKIM MODU — F-SEC-06'nın dördüncüsü.
 *
 * ================================================================
 * BAŞKA EKRANA YÖNLENDİRMEK YALAN OLURDU
 * ================================================================
 * İlk yazımda burada "Bakım ekranına git" düğmesi vardı. Ölçtüm:
 * **panelde bakım ekranı YOK.** `bakimAc` / `bakimKapat` istemcileri
 * `api.ts` içinde duruyor ve hiçbir ekran onları çağırmıyor — bu
 * depoda yedi kez ölçülen *"mekanizma var, besleyen yok"* sınıfının
 * bir örneği daha.
 *
 * Var olmayan bir ekrana yönlendiren düğme, çalışmayan bir düğmeden
 * kötüdür: kullanıcı hatayı kendinde arar.
 *
 * ================================================================
 * ⚠️ BAKIM MODU GİRİŞİ KAPATMIYOR
 * ================================================================
 * Şartnamedeki cümle 05.09.2026'da düzeltildi. Kapatsaydı sistemi
 * kimse açamazdı: giriş yapamayan kişi teknik yönetici olduğunu
 * kanıtlayamaz. Kapanan şey **girişten sonrası**.
 */
function BakimKarti() {
  const qc = useQueryClient();
  const [asama, setAsama] = useState<"kapali" | "onay">("kapali");
  const [baslik, setBaslik] = useState("");
  const [mesaj, setMesaj] = useState("");
  const { hataYakala, kapi } = useSudoKapisi();

  const durum = useQuery({ queryKey: ["bakim-durum"], queryFn: bakimDurumu, retry: false });

  const ac = useMutation({
    mutationFn: () => bakimAc({ baslik, mesaj }),
    onError: hataYakala(() => ac.mutate()),
    onSuccess: async () => {
      setAsama("kapali");
      setBaslik("");
      setMesaj("");
      await qc.invalidateQueries({ queryKey: ["bakim-durum"] });
      await qc.invalidateQueries({ queryKey: ["bakim-durum-bant"] });
    },
  });

  const kapat = useMutation({
    mutationFn: () => bakimKapat(),
    onError: hataYakala(() => kapat.mutate()),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["bakim-durum"] });
      await qc.invalidateQueries({ queryKey: ["bakim-durum-bant"] });
    },
  });

  const acik = durum.data?.acik === true;

  return (
    <div className="border-notr-200 flex flex-col gap-2 border-t py-3">
      {kapi}
      <div>
        <p className="text-notr-900 text-sm font-medium">
          Bakım modu {acik && <span className="text-uyari-800">— şu an AÇIK</span>}
        </p>
        <p className="text-notr-600 mt-0.5 text-xs">
          Giriş açık kalır; girişten sonrası, bakım yetkisi olmayan herkese kapanır. Kullanıcı
          yazdığınız mesajı aynen görür.
        </p>
      </div>

      {acik ? (
        <div className="bg-uyari-50 rounded-alan flex flex-col gap-2 p-3">
          <p className="text-uyari-800 text-sm">
            {durum.data?.baslik} — {durum.data?.mesaj}
          </p>
          <div>
            <Buton
              onClick={() => kapat.mutate()}
              yukleniyor={kapat.isPending}
              yukleniyorMetni="Kapatılıyor"
            >
              Bakım modunu kapat
            </Buton>
          </div>
        </div>
      ) : asama === "kapali" ? (
        <div>
          <Buton gorunum="ikincil" onClick={() => setAsama("onay")}>
            Bakım moduna al
          </Buton>
        </div>
      ) : (
        <div className="bg-uyari-50 rounded-alan flex flex-col gap-2 p-3">
          <Alan
            etiket="Başlık"
            yardim="Kullanıcıya aynen gösterilir."
            value={baslik}
            onChange={(e) => setBaslik(e.target.value)}
          />
          <Alan
            etiket="Mesaj"
            yardim="Ne olduğunu ve ne zaman biteceğini yazın. Boş bir bakım mesajı, kullanıcıya sistemin bozulduğunu düşündürür."
            value={mesaj}
            onChange={(e) => setMesaj(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Buton
              onClick={() => ac.mutate()}
              yukleniyor={ac.isPending}
              yukleniyorMetni="Açılıyor"
              /*
                Uzunluk kurali SUNUCUDA (baslik >= 3, mesaj >= 10).
                Burada yalnizca BOS gecilmesi engelleniyor; kesin
                siniri ekran tekrarlasaydi, sunucu degistiginde
                sessizce ayrisirdi.
              */
              disabled={baslik.trim().length === 0 || mesaj.trim().length === 0}
            >
              Bakım moduna al
            </Buton>
            <Buton gorunum="ikincil" onClick={() => setAsama("kapali")}>
              Vazgeç
            </Buton>
          </div>
        </div>
      )}

      {(ac.isError || kapat.isError) && (
        <p role="alert" className="text-hata-700 text-sm">
          {(() => {
            const h = ac.error ?? kapat.error;
            return h instanceof ApiHatasi ? h.message : "Bakım modu değiştirilemedi.";
          })()}
        </p>
      )}
    </div>
  );
}

export function AcilDurum() {
  return (
    <Kart
      baslik="Acil durum"
      ton="uyari"
      aciklama="Toplu etkili işlemler. Her biri gerekçe ister, şifre doğrulaması ister ve denetim kaydına silinemez biçimde geçer."
    >
      <div className="flex flex-col">
        {ISLEMLER.map((i) => (
          <AcilKart key={i.anahtar} islem={i} />
        ))}

        <BakimKarti />
      </div>
    </Kart>
  );
}
